import React from "react"
import { useEffect, useState, useRef } from "react"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"
import { SkeletonCard, SkeletonText, SkeletonButton } from "../components/LoadingSpinner"
import ReadingScene from "../components/ReadingScene"
import ExerciseVocabulary from "../components/ExerciseVocabulary"
import { scrollToTop, scrollResultIntoView } from "../utils/scroll"

function stripWord(token) {
  return String(token || "").replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")
}

function ReadingPassage({ passage, selectedText, savedWords, onSelect }) {
  const selectedNorm = (selectedText || "").trim().toLowerCase()
  const selectedParts = selectedNorm ? selectedNorm.split(/\s+/) : []
  const saved = new Set((savedWords || []).map((word) => String(word).toLowerCase()))
  const tokens = String(passage || "").split(/(\s+)/)

  const handleMouseUp = () => {
    const text = window.getSelection()?.toString().trim()
    if (text) onSelect(text)
  }

  return (
    <div className="reading-passage-text select-text" onMouseUp={handleMouseUp}>
      {tokens.map((token, index) => {
        if (/^\s+$/.test(token)) {
          return <span key={index}>{token}</span>
        }
        const core = stripWord(token)
        if (!core) {
          return <span key={index}>{token}</span>
        }
        const key = core.toLowerCase()
        const isSelected = selectedParts.includes(key) || key === selectedNorm
        const isSaved = saved.has(key)
        return (
          <button
            type="button"
            key={index}
            className={`reading-word${isSelected ? " is-selected" : ""}${isSaved ? " is-saved" : ""}`}
            aria-pressed={isSelected}
            onClick={() => onSelect(selectedNorm === key ? "" : core)}
          >
            {token}
          </button>
        )
      })}
    </div>
  )
}

export default function Reading() {
  const { t } = useLanguage()
  const [view, setView] = useState("picker") // picker | practice
  const [lessons, setLessons] = useState([])
  const [lessonsLoading, setLessonsLoading] = useState(true)
  const [exercise, setExercise] = useState(null)
  const [answers, setAnswers] = useState({})
  const [score, setScore] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [attempts, setAttempts] = useState([])
  const [showAttemptHistory, setShowAttemptHistory] = useState(false)
  const [readingTime, setReadingTime] = useState(0)
  const [isReading, setIsReading] = useState(false)
  const [selectedText, setSelectedText] = useState("")
  const [showDictionary, setShowDictionary] = useState(false)
  const [difficulty, setDifficulty] = useState("all")
  const [showResubmitModal, setShowResubmitModal] = useState(false)
  const [resubmitInfo, setResubmitInfo] = useState({ attempts: 0, bestScore: 0 })
  const [savedWords, setSavedWords] = useState([])
  const shapesRef = useRef(null)
  const sessionStartedAtRef = useRef(Date.now())
  const lessonsLoadedRef = useRef(false)

  const loadLessons = async (selectedDifficulty = difficulty) => {
    if (!lessonsLoadedRef.current) setLessonsLoading(true)
    setError("")
    try {
      const data = await api.listReadingExercises(
        selectedDifficulty !== "all" ? selectedDifficulty : null
      )
      setLessons(data.exercises || [])
      lessonsLoadedRef.current = true
    } catch (e) {
      setError(e.message)
      setLessons([])
    } finally {
      setLessonsLoading(false)
    }
  }

  const loadExercise = async ({ exerciseId, title, selectedDifficulty = difficulty } = {}) => {
    scrollToTop()
    setLoading(true)
    setError("")
    setScore(null)
    setAnswers({})
    setShowAttemptHistory(false)
    setReadingTime(0)
    setIsReading(false)
    setSelectedText("")
    setShowDictionary(false)
    setSavedWords([])
    sessionStartedAtRef.current = Date.now()

    try {
      const ex = await api.readingExercise({
        difficulty: selectedDifficulty !== "all" ? selectedDifficulty : null,
        exerciseId: exerciseId || null,
        title: title || null,
      })
      setExercise(ex)
      setView("practice")
      setAttempts([])
      scrollToTop()

      if (ex.lesson_id) {
        try {
          const history = await api.getReadingAttempts(ex.lesson_id)
          setAttempts(history.attempts || [])
        } catch {
          /* no prior attempts */
        }
      }
    } catch (e) {
      setError(e.message)
      setView("picker")
    } finally {
      setLoading(false)
    }
  }

  const backToLessons = () => {
    setView("picker")
    setExercise(null)
    setScore(null)
    setAnswers({})
    setAttempts([])
    setIsReading(false)
    setShowAttemptHistory(false)
    setError("")
    scrollToTop()
  }

  useEffect(() => {
    loadLessons()
  }, [])

  useEffect(() => {
    let interval = null
    if (isReading) {
      interval = setInterval(() => {
        setReadingTime((time) => time + 1)
      }, 1000)
    } else if (!isReading && readingTime !== 0) {
      clearInterval(interval)
    }
    return () => clearInterval(interval)
  }, [isReading, readingTime])

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)")
    const onMove = (e) => {
      if (!mq.matches) return
      const layer = shapesRef.current
      if (!layer) return
      const x = (e.clientX / window.innerWidth - 0.5) * 2
      const y = (e.clientY / window.innerHeight - 0.5) * 2
      layer.style.transform = `rotateX(${y * -3}deg) rotateY(${x * 5}deg)`
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const startReading = () => {
    setIsReading(true)
    setReadingTime(0)
  }

  const stopReading = () => {
    setIsReading(false)
  }

  const difficultyChipClass = (level) =>
    `reading-chip reading-chip--${(level || "default").toLowerCase()}`

  const translateDifficulty = (level) => {
    switch (level) {
      case "Beginner":
        return t("beginner")
      case "Intermediate":
        return t("intermediate")
      case "Advanced":
        return t("advanced")
      default:
        return level
    }
  }

  const submit = async (force = false) => {
    if (!exercise) return

    if (attempts.length > 0 && !force) {
      const bestScore = Math.max(...attempts.map((a) => a.score ?? 0))
      setResubmitInfo({
        attempts: attempts.length,
        bestScore: Number.isFinite(bestScore) ? bestScore : 0,
      })
      setShowResubmitModal(true)
      return
    }

    setShowResubmitModal(false)
    setSubmitting(true)
    try {
      const elapsed = Math.max(
        readingTime,
        Math.round((Date.now() - sessionStartedAtRef.current) / 1000)
      )
      const payload = {
        lesson_id: exercise.lesson_id,
        time_spent: Math.max(1, elapsed),
        answers: Object.entries(answers).map(([id, answer]) => ({ id: Number(id), answer })),
      }
      const res = await api.submitReading(payload)
      setScore(res.score)
      scrollResultIntoView()

      if (exercise.lesson_id) {
        const history = await api.getReadingAttempts(exercise.lesson_id)
        setAttempts(history.attempts || [])
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  const getScoreColor = (s) => {
    if (s >= 80) return "text-primary"
    if (s >= 60) return "text-amber-600 dark:text-amber-400"
    return "text-destructive"
  }

  const getScoreBgColor = (s) => {
    if (s >= 80) return "bg-primary/10 border-primary/30"
    if (s >= 60) return "bg-amber-500/10 border-amber-500/30"
    return "bg-destructive/10 border-destructive/30"
  }

  if (lessonsLoading || (view === "practice" && loading)) {
    return (
      <div className="min-h-screen reading-page-bg relative overflow-hidden">
        <ReadingScene />
        <div className="relative z-10 p-4 sm:p-6 max-w-5xl mx-auto w-full min-w-0">
          <div className="animate-pulse space-y-6">
            <SkeletonText lines={2} className="max-w-sm" />
            <div className="flex gap-2 flex-wrap">
              <SkeletonButton className="w-20" />
              <SkeletonButton className="w-24" />
              <SkeletonButton className="w-24" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SkeletonCard className="h-36 rounded-2xl" />
              <SkeletonCard className="h-36 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error && view === "picker" && lessons.length === 0) {
    return (
      <div className="min-h-screen reading-page-bg relative overflow-hidden flex items-center justify-center p-4">
        <ReadingScene />
        <div className="relative z-10 reading-glass p-6 sm:p-8 max-w-md w-full text-center">
          <h3 className="text-base font-semibold text-foreground mb-2">{t("readingErrorLoading")}</h3>
          <p className="text-muted-foreground mb-4 text-sm break-words">{error}</p>
          <button
            type="button"
            onClick={() => loadLessons()}
            className="button-glow bg-gradient-to-r from-primary to-accent text-primary-foreground px-4 py-2 rounded-xl hover:opacity-95 transition-all text-sm font-medium"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  const levelOptions = [
    { key: "all", label: t("readingAllLevels") },
    { key: "Beginner", label: t("beginner") },
    { key: "Intermediate", label: t("intermediate") },
    { key: "Advanced", label: t("advanced") },
  ]

  if (view === "picker") {
    return (
      <div className="min-h-screen reading-page-bg relative overflow-hidden">
        <ReadingScene layerRef={shapesRef} />

        <div key="picker" className="relative z-10 reading-scene view-stage">
          <div className="reading-picker max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 w-full min-w-0">
            <header className="reading-hero-minimal reading-picker-hero mb-6 md:mb-8">
              <p className="reading-picker-eyebrow">{t("reading")}</p>
              <h1 className="reading-hero-title">{t("readingChooseLesson")}</h1>
              <div className="reading-hero-accent" aria-hidden />
              <p className="reading-picker-lead">{t("readingPickShelf")}</p>
            </header>

            <section className="reading-shelf-filters mb-6 sm:mb-8">
              <p className="reading-shelf-filters-label">{t("readingChooseLevel")}</p>
              <div className="reading-shelf-rail" role="tablist" aria-label={t("readingChooseLevel")}>
                {levelOptions.map((level) => {
                  const isActive = difficulty === level.key
                  return (
                    <button
                      key={level.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => {
                        setDifficulty(level.key)
                        loadLessons(level.key)
                      }}
                      className={`reading-shelf-tab ${isActive ? "reading-shelf-tab--active" : ""}`}
                    >
                      {level.label}
                    </button>
                  )
                })}
              </div>
            </section>

            <section className="w-full min-w-0">
              <div className="reading-library-header">
                <h2 className="reading-library-title">{t("reading")}</h2>
                <p className="reading-library-count">
                  {lessons.length}{" "}
                  {lessons.length === 1 ? t("readingLesson") : t("readingLessons")}
                </p>
              </div>

              {error && <p className="text-sm text-destructive mb-4">{error}</p>}

              <div className="reading-library-grid">
                {lessons.map((lesson) => (
                  <button
                    key={lesson.id || lesson.title}
                    type="button"
                    onClick={() =>
                      loadExercise({
                        exerciseId: lesson.id,
                        title: lesson.title,
                        selectedDifficulty: difficulty,
                      })
                    }
                    className="reading-lesson-card"
                  >
                    <span className="reading-lesson-rail" aria-hidden />
                    <div className="reading-lesson-body">
                      <div className="reading-lesson-kicker">
                        <span className={difficultyChipClass(lesson.difficulty)}>
                          {translateDifficulty(lesson.difficulty)}
                        </span>
                        <span className="reading-lesson-stats">
                          <span>
                            ~{lesson.estimated_duration} {t("readingMinRead")}
                          </span>
                          <span>
                            {lesson.questions_count} {t("readingQuestionsCount")}
                          </span>
                          {lesson.vocabulary_count > 0 && (
                            <span>
                              {lesson.vocabulary_count} {t("speakingVocabWords")}
                            </span>
                          )}
                        </span>
                      </div>
                      <h3 className="reading-lesson-title">{lesson.title}</h3>
                      {lesson.instructions && (
                        <p className="reading-lesson-blurb">{lesson.instructions}</p>
                      )}
                      <span className="reading-lesson-cta">
                        {t("readingStartLesson")}
                        <span className="reading-lesson-cta-icon" aria-hidden>
                          →
                        </span>
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {lessons.length === 0 && !error && (
                <div className="reading-library-empty">
                  {t("readingNoLessons")}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    )
  }

  if (!exercise) return null

  const questions = Array.isArray(exercise.questions)
    ? [...exercise.questions].sort((a, b) => {
        const ao = typeof a.order === "number" ? a.order : a.id ?? 0
        const bo = typeof b.order === "number" ? b.order : b.id ?? 0
        return ao - bo
      })
    : []

  const questionsWithMeta = questions.map((q, index) => {
    const hasOptions = Array.isArray(q.options) && q.options.length > 0
    const isTrueFalse =
      hasOptions &&
      q.options.length === 2 &&
      q.options.every((o) => ["True", "False"].includes(o))

    let type = "fillBlank"
    if (hasOptions) {
      type = isTrueFalse ? "trueFalse" : "multipleChoice"
    }

    return {
      ...q,
      _index: index,
      _hasOptions: hasOptions,
      _isTrueFalse: isTrueFalse,
      _type: type,
    }
  })

  const totalQuestions = questionsWithMeta.length
  const answeredQuestions = Object.keys(answers).length
  const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0

  const questionGroups = [
    {
      key: "multipleChoice",
      label: t("readingMultipleChoice"),
      description: t("readingMultipleChoiceDesc"),
    },
    {
      key: "trueFalse",
      label: t("readingTrueFalse"),
      description: t("readingTrueFalseDesc"),
    },
    {
      key: "fillBlank",
      label: t("readingFillBlank"),
      description: t("readingFillBlankDesc"),
    },
  ]

  return (
    <div className="min-h-screen reading-page-bg relative overflow-hidden">
      <ReadingScene
        layerRef={shapesRef}
        active={isReading}
      />

      <div key="practice" className="relative z-10 reading-scene view-stage">
        <div className="reading-practice">
          <button type="button" onClick={backToLessons} className="reading-back">
            ← {t("readingBackToLessons")}
          </button>

          <section className="reading-hud">
            <div className="reading-hud-top">
              <div className="min-w-0">
                <h2 className="reading-hud-title">{exercise.title}</h2>
                <div className="reading-hud-chips">
                  <span className={difficultyChipClass(exercise.difficulty)}>
                    {translateDifficulty(exercise.difficulty)}
                  </span>
                  <span className="reading-chip reading-chip--ghost">
                    ~{exercise.estimated_duration} {t("readingMinRead")}
                  </span>
                  <span className="reading-chip reading-chip--ghost">
                    {totalQuestions} {t("readingQuestionsCount")}
                  </span>
                  {isReading && (
                    <span className="reading-chip reading-chip--live tabular-nums">
                      {formatTime(readingTime)}
                    </span>
                  )}
                </div>
              </div>
              <div className="reading-hud-actions">
                {!isReading ? (
                  <button type="button" onClick={startReading} className="reading-btn reading-btn--primary">
                    {t("readingStartTimer")}
                  </button>
                ) : (
                  <button type="button" onClick={stopReading} className="reading-btn reading-btn--ghost">
                    {t("readingPauseTimer")}
                  </button>
                )}
                {attempts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAttemptHistory(!showAttemptHistory)}
                    className="reading-btn reading-btn--ghost"
                  >
                    {showAttemptHistory ? t("readingHide") : t("readingShowHistory")} ({attempts.length})
                  </button>
                )}
              </div>
            </div>
            {exercise.instructions && (
              <p className="reading-hud-lead">{exercise.instructions}</p>
            )}
            {showAttemptHistory && attempts.length > 0 && (
              <div className="reading-history">
                {attempts.map((a, i) => (
                  <div key={i} className="reading-history-row">
                    <span>{t("readingAttempt")} {a.attempt_number ?? i + 1}</span>
                    <span className="tabular-nums">{a.score}%</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="reading-practice-layout">
            <div className="reading-studio">
              <section className="reading-reader">
                <div className="reading-reader-bar">
                  <span className="reading-reader-status" aria-hidden />
                  <h3>{t("readingPassageTitle")}</h3>
                  <p className="reading-reader-hint">{t("vocabSelectHint")}</p>
                </div>
                <div className="reading-reader-body">
                  {exercise.passage ? (
                    <ReadingPassage
                      passage={exercise.passage}
                      selectedText={showDictionary ? selectedText : ""}
                      savedWords={savedWords}
                      onSelect={(text) => {
                        setSelectedText(text)
                        setShowDictionary(Boolean(text))
                      }}
                    />
                  ) : (
                    <div className="reading-reader-empty">
                      <p className="font-medium mb-1">{t("readingNoPassage")}</p>
                      <p className="text-sm text-muted-foreground mb-4">{t("readingChooseAnother")}</p>
                      <button type="button" onClick={backToLessons} className="reading-btn reading-btn--primary">
                        {t("readingBackToLessons")}
                      </button>
                    </div>
                  )}
                </div>
              </section>
              <aside className="reading-vocab">
                <ExerciseVocabulary
                  skill="reading"
                  lessonId={exercise.lesson_id}
                  lessonTitle={exercise.title}
                  sourceRef={exercise.id}
                  selectedText={showDictionary ? selectedText : ""}
                  onClearSelection={() => {
                    setSelectedText("")
                    setShowDictionary(false)
                  }}
                  onWordsChange={setSavedWords}
                  contextText={exercise.passage}
                  suggestions={exercise.vocabulary || []}
                />
              </aside>
            </div>

            <section className="reading-desk">
              <div className="reading-desk-head">
                <div className="min-w-0">
                  <h3>{t("readingComprehensionQuestions")}</h3>
                  <p>{t("readingAnswerFromPassage")}</p>
                </div>
                <div className="reading-progress">
                  <span className="tabular-nums">
                    {answeredQuestions}/{totalQuestions} {t("readingAnswered")}
                  </span>
                  <div className="reading-progress-track" aria-hidden>
                    <div className="reading-progress-fill" style={{ width: `${progressPercentage}%` }} />
                  </div>
                </div>
              </div>

              <div className="reading-groups">
                {questionGroups.map((group) => {
                  const groupQuestions = questionsWithMeta.filter((q) => q._type === group.key)
                  if (groupQuestions.length === 0) return null

                  return (
                    <div key={group.key} className="reading-group">
                      <div className="reading-group-head">
                        <div>
                          <h4>{group.label}</h4>
                          <p className="hidden sm:block">{group.description}</p>
                        </div>
                        <span>
                          {groupQuestions.length}{" "}
                          {groupQuestions.length > 1 ? t("readingQuestionsPlural") : t("readingQuestionSingular")}
                        </span>
                      </div>

                      <div className="reading-q-list">
                        {groupQuestions.map((q) => (
                          <div key={q.id} className="reading-q">
                            <span className="reading-q-num">{q._index + 1}</span>
                            <div className="reading-q-body">
                              <p className="reading-q-text">{q.q || q.question}</p>
                              {q._hasOptions ? (
                                <div
                                  className={`reading-mcq-options ${q._isTrueFalse ? "reading-mcq-options--tf" : ""}`}
                                >
                                  {q.options.map((option, optIndex) => (
                                    <label
                                      key={optIndex}
                                      className={`reading-mcq-option${answers[q.id] === option ? " is-on" : ""}`}
                                    >
                                      <input
                                        type="radio"
                                        name={`q_${q.id}`}
                                        value={option}
                                        checked={answers[q.id] === option}
                                        onChange={(e) =>
                                          setAnswers({ ...answers, [q.id]: e.target.value })
                                        }
                                      />
                                      <span>
                                        {q._isTrueFalse
                                          ? option === "True"
                                            ? t("readingTrue")
                                            : t("readingFalse")
                                          : option}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  className="reading-fill"
                                  placeholder={t("readingWriteAnswer")}
                                  value={answers[q.id] ?? ""}
                                  onChange={(e) =>
                                    setAnswers({ ...answers, [q.id]: e.target.value })
                                  }
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="reading-actions">
                <button
                  type="button"
                  onClick={() => submit()}
                  disabled={submitting || answeredQuestions === 0}
                  className="reading-btn reading-btn--primary reading-btn--wide"
                >
                  {submitting ? t("submitting") : t("submitAnswers")}
                </button>
                <button type="button" onClick={backToLessons} className="reading-btn reading-btn--ghost">
                  {t("readingBackToLessons")}
                </button>
              </div>

              {score !== null && (
                <div className={`reading-score ui-reveal ${getScoreBgColor(score)}`}>
                  <h3>{t("readingChapterComplete")}</h3>
                  <p className={`reading-score-num ${getScoreColor(score)}`}>{score}%</p>
                  <p>
                    {score >= 80
                      ? t("readingScoreExcellent")
                      : score >= 60
                        ? t("readingScoreGood")
                        : t("readingScoreKeep")}
                  </p>
                  {attempts.length > 1 && (
                    <p className="reading-score-best">
                      {t("readingBestScore")} {Math.max(...attempts.map((a) => a.score))}%
                    </p>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {showResubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
          <div className="reading-glass w-full max-w-md p-5 sm:p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">{t("readingResubmitTitle")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("readingResubmitAlready")} {resubmitInfo.attempts}{" "}
                {resubmitInfo.attempts === 1 ? t("readingResubmitTime") : t("readingResubmitTimes")}. {t("readingResubmitBest")}{" "}
                <span className="font-semibold text-primary">{Math.round(resubmitInfo.bestScore)}%</span>.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {t("readingResubmitNote")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowResubmitModal(false)}
                className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-border text-foreground hover:bg-muted font-medium"
              >
                {t("readingGoBack")}
              </button>
              <button
                type="button"
                onClick={() => submit(true)}
                className="button-glow flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold"
              >
                {t("readingSubmitAnyway")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
